import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import Modal from '../common/Modal.jsx';
import { OutlinedButton } from '../common/Buttons.jsx';
import SearchableSelectField from '../common/SearchableSelectField.jsx';
import { useToast } from '../common/Toast';
import { planService } from '../../services/planService.js';
import { toLocalYMD } from '../../utils/dateUtils';

const buildErrorMsg = (e) => {
  const data = e?.response?.data;
  if (!data) return e?.message || 'Error desconocido.';
  if (typeof data === 'string') return data;
  if (data?.detail) return data.detail;
  const pairs = Object.entries(data)
    .filter(([k]) => k !== 'non_field_errors')
    .map(([, v]) => (Array.isArray(v) ? v.join(' ') : String(v || '')))
    .filter(Boolean);
  const nonField = Array.isArray(data?.non_field_errors) ? data.non_field_errors.join(' ') : (data?.non_field_errors || '');
  return [nonField, ...pairs].filter(Boolean).join('\n') || e?.message || 'Error desconocido.';
};

/**
 * Modal para establecer el mantenimiento inicial de un equipo.
 * Props:
 *   open        {bool}     - si el modal está abierto
 *   onClose     {func}     - callback para cerrar
 *   equipoId    {number}   - ID del equipo
 *   equipoLabel {string}   - etiqueta descriptiva del equipo (para el título)
 *   unidad      {string}   - nombre del tipo de contador (ej: "Horas", "Kilómetros")
 *   onSuccess   {func}     - callback llamado con la respuesta de la API al guardar con éxito
 */
const ModalMantenimientoInicial = ({ open, onClose, equipoId, equipoLabel, unidad, onSuccess }) => {
  const theme = useTheme();
  const { toast } = useToast();

  const [form, setForm] = useState({ fecha: '', odometro: '', odometro_teorico: '', rutina: '', observaciones: '' });
  const [info, setInfo] = useState(null);
  const [rutinasPlan, setRutinasPlan] = useState([]);
  const [rutinaMap, setRutinaMap] = useState({});
  const [teoricosCompat, setTeoricosCompat] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [loadingGuardar, setLoadingGuardar] = useState(false);
  // Cada lectura tipeada dispara un pedido; si la respuesta de un valor parcial ("296")
  // llega después de la del final ("2966") pisaría el teórico. Se descartan las viejas.
  const pedidoSeq = useRef(0);
  const debounceRef = useRef(null);
  const teoricoManual = useRef(false);

  useEffect(() => {
    if (!open || !equipoId) return;
    setForm({ fecha: toLocalYMD(new Date()), odometro: '', odometro_teorico: '', rutina: '', observaciones: '' });
    setTeoricosCompat([]);
    teoricoManual.current = false;
    pedidoSeq.current += 1;
    clearTimeout(debounceRef.current);
    cargarPlanInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, equipoId]);

  const cargarPlanInfo = async () => {
    setLoadingPlan(true);
    setInfo(null);
    setRutinasPlan([]);
    setRutinaMap({});
    try {
      const asignados = await planService.obtenerAsignados({ equipo: equipoId });
      const pa = (asignados.results || asignados).find(a => String(a.equipo) === String(equipoId));
      if (pa) {
        setInfo({
          odometro_teorico: pa.odometro_teorico,
          fecha_ultimo: pa.fecha_ultimo,
          odometro_ultimo: pa.odometro_ultimo,
          tipo_ultimo: pa.tipo_ultimo,
        });
        try {
          const r = await planService.obtenerRutinasPorPlan(pa.plan);
          const lista = (r.results || r || []).map(rr => ({
            id: rr.id,
            nombre: rr.nombre || rr.descripcion || `Rutina ${rr.id}`,
            frecuencia_valor: rr.frecuencia_valor,
            tipo_contador_nombre: rr.tipo_contador_nombre,
          }));
          setRutinasPlan(lista);
          const mapa = {};
          lista.forEach(r => { mapa[String(r.id)] = r; });
          setRutinaMap(mapa);
        } catch {}
      }
    } catch {}
    setLoadingPlan(false);
  };

  // Trae los teóricos válidos para la lectura y el más cercano a ella (el sugerido).
  const obtenerTeoricos = async (odometro) => {
    const res = await planService.compatibleTeoricos({ equipo: String(equipoId), odometro: Number(odometro) });
    const teoricos = res.teoricos || [];
    const num = Number(odometro);
    const sugerido = teoricos.length > 0
      ? teoricos.reduce((prev, curr) => (Math.abs(curr.valor - num) < Math.abs(prev.valor - num) ? curr : prev)).valor
      : null;
    return { teoricos, sugerido };
  };

  const cargarTeoricosCompat = async (odometro) => {
    const seq = ++pedidoSeq.current;
    if (!equipoId || odometro === '' || isNaN(Number(odometro))) {
      setTeoricosCompat([]);
      return;
    }
    try {
      const { teoricos, sugerido } = await obtenerTeoricos(odometro);
      if (seq !== pedidoSeq.current) return;
      setTeoricosCompat(teoricos);
      if (sugerido !== null) setForm(v => ({ ...v, odometro_teorico: String(sugerido) }));
    } catch {
      if (seq === pedidoSeq.current) setTeoricosCompat([]);
    }
  };

  const onCambioOdometro = (valor) => {
    // La lectura cambió: el teórico anterior ya no corresponde hasta recalcularlo.
    teoricoManual.current = false;
    pedidoSeq.current += 1;
    setForm(v => ({ ...v, odometro: valor, odometro_teorico: '' }));
    setTeoricosCompat([]);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cargarTeoricosCompat(valor), 350);
  };

  const onSubmit = async () => {
    if (loadingGuardar) return;
    if (!equipoId) { onClose(); return; }
    if (form.odometro === '' || form.odometro === null || isNaN(Number(form.odometro)) || Number(form.odometro) < 0) {
      toast.warning(`Indicá una lectura de ${unidad || 'odómetro'} inicial válida (0 o mayor).`);
      return;
    }
    setLoadingGuardar(true);
    try {
      // Relee la lectura completa y revalida el teórico antes de guardar.
      clearTimeout(debounceRef.current);
      pedidoSeq.current += 1;
      const { teoricos, sugerido } = await obtenerTeoricos(form.odometro);
      const elegido = form.odometro_teorico;
      const esValido = teoricos.some(t => String(t.valor) === String(elegido));
      const corresponde = teoricoManual.current ? esValido : String(sugerido) === String(elegido);
      if (teoricos.length > 0 && !corresponde) {
        setTeoricosCompat(teoricos);
        if (!teoricoManual.current || !esValido) {
          setForm(v => ({ ...v, odometro_teorico: String(sugerido) }));
        }
        toast.warning(`El teórico no correspondía a la lectura de ${Number(form.odometro).toLocaleString('es-AR')} ${unidad || ''}. Se actualizó a ${sugerido}: revisalo y volvé a guardar.`);
        return;
      }
      const payload = { equipo: equipoId, odometro: Number(form.odometro) };
      if (form.fecha) payload.fecha = form.fecha;
      if (form.odometro_teorico) payload.odometro_teorico = Number(form.odometro_teorico);
      if (form.rutina) payload.rutina = Number(form.rutina);
      if (form.observaciones) payload.observaciones = form.observaciones;

      const res = await planService.setInicial(payload);
      onClose();
      if (onSuccess) await onSuccess(res);

      if (res?.proximo_generado) {
        toast.success('Mantenimiento inicial registrado. Se recalculó la planificación del equipo.');
      } else if (res?.proximo_error) {
        toast.warning('Mantenimiento inicial registrado. No se pudo calcular el próximo automáticamente — podés configurarlo manualmente desde el plan del equipo.');
      } else {
        toast.success('Mantenimiento inicial registrado.');
      }
    } catch (e) {
      toast.error(buildErrorMsg(e));
    } finally {
      setLoadingGuardar(false);
    }
  };

  if (!open) return null;

  const title = `Indicar mantenimiento inicial`;
  const unidadLabel = unidad || 'unidades';

  const rutinaUltimaLabel = (() => {
    if (!info?.tipo_ultimo) return '-';
    const r = rutinaMap[String(info.tipo_ultimo)];
    return r ? (r.nombre || `Rutina ${info.tipo_ultimo}`) : `Rutina ID ${info.tipo_ultimo}`;
  })();

  return (
    <Modal title={title} onClose={onClose} size="md" maxHeight="92vh">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '68vh' }}>
        <div style={{ overflowY: 'auto', paddingRight: 4 }}>

          {equipoLabel && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '8px 10px', borderRadius: 6, background: theme.colors.bgSecundario, border: `1px solid ${theme.colors.borderDefault}` }}>
              <i className="bi bi-truck" style={{ color: theme.colors.txtSecondary, fontSize: 14 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: theme.colors.txtPrincipal }}>{equipoLabel}</span>
            </div>
          )}

          {/* Estado actual del ancla */}
          <div style={{ marginBottom: 10, padding: 10, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, background: theme.colors.bgSecundario }}>
            <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Estado actual</div>
            {loadingPlan ? (
              <div style={{ color: theme.colors.txtSecondary, fontSize: 12 }}>Cargando...</div>
            ) : info ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 8, fontSize: 12 }}>
                <div><small>Teórico actual</small><br />{info.odometro_teorico != null ? `${Number(info.odometro_teorico).toLocaleString('es-AR')} ${unidadLabel}` : '-'}</div>
                <div><small>Fecha último</small><br />{info.fecha_ultimo || '-'}</div>
                <div><small>Última lectura</small><br />{info.odometro_ultimo != null ? `${Number(info.odometro_ultimo).toLocaleString('es-AR')} ${unidadLabel}` : '-'}</div>
                <div><small>Rutina última</small><br />{rutinaUltimaLabel}</div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: theme.colors.txtSecondary }}>No se encontró configuración previa del ancla para este equipo.</div>
            )}
          </div>

          {/* Formulario */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 8 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4, fontWeight: 600, color: theme.colors.txtPrincipal }}>Fecha</label>
              <input
                type="date"
                value={form.fecha}
                onChange={(e) => setForm(v => ({ ...v, fecha: e.target.value }))}
                style={{ width: '100%', height: 30, borderRadius: 6, border: `1px solid ${theme.colors.borderDefault}`, background: theme.colors.surface, color: theme.colors.txtPrincipal, padding: '0 8px', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4, fontWeight: 600, color: theme.colors.txtPrincipal }}>{unidadLabel} inicial</label>
              <input
                type="number"
                value={form.odometro}
                onChange={(e) => onCambioOdometro(e.target.value)}
                style={{ width: '100%', height: 30, borderRadius: 6, border: `1px solid ${theme.colors.borderDefault}`, background: theme.colors.surface, color: theme.colors.txtPrincipal, padding: '0 8px', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4, fontWeight: 600, color: theme.colors.txtPrincipal }}>Teórico correspondiente</label>
              {teoricosCompat.length > 0 ? (
                <SearchableSelectField
                  options={teoricosCompat.map(t => ({ value: String(t.valor), label: `${t.valor} ${unidadLabel}` }))}
                  value={form.odometro_teorico}
                  onChange={(value) => { teoricoManual.current = true; setForm(v => ({ ...v, odometro_teorico: value })); }}
                  placeholder="Seleccionar teórico..."
                  showAllOnFocus
                  size="sm"
                />
              ) : (
                <input
                  type="text"
                  value={form.odometro_teorico}
                  placeholder={`Ingresá la lectura en ${unidadLabel.toLowerCase()} para ver opciones`}
                  disabled
                  style={{ width: '100%', height: 30, borderRadius: 6, border: `1px solid ${theme.colors.borderDefault}`, background: theme.colors.bgSecundario, color: theme.colors.txtSecondary, padding: '0 8px', fontSize: 12 }}
                />
              )}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4, fontWeight: 600, color: theme.colors.txtPrincipal }}>Rutina (opcional)</label>
              {rutinasPlan.length > 0 ? (
                <SearchableSelectField
                  options={rutinasPlan}
                  value={form.rutina}
                  onChange={(value) => setForm(v => ({ ...v, rutina: value }))}
                  placeholder="Seleccionar automáticamente"
                  getOptionValue={(option) => String(option.id)}
                  getOptionLabel={(option) => `${option.nombre || ''}${option.frecuencia_valor ? ` (${option.frecuencia_valor} ${option.tipo_contador_nombre || ''})` : ''}`.trim() || `Rutina ${option.id}`}
                  showAllOnFocus
                  size="sm"
                />
              ) : (
                <input
                  type="number"
                  value={form.rutina}
                  onChange={(e) => setForm(v => ({ ...v, rutina: e.target.value }))}
                  placeholder="Dejar vacío para selección automática"
                  style={{ width: '100%', height: 30, borderRadius: 6, border: `1px solid ${theme.colors.borderDefault}`, background: theme.colors.surface, color: theme.colors.txtPrincipal, padding: '0 8px', fontSize: 12 }}
                />
              )}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4, fontWeight: 600, color: theme.colors.txtPrincipal }}>Observaciones</label>
              <textarea
                value={form.observaciones}
                onChange={(e) => setForm(v => ({ ...v, observaciones: e.target.value }))}
                style={{ width: '100%', minHeight: 72, maxHeight: 96, borderRadius: 6, border: `1px solid ${theme.colors.borderDefault}`, background: theme.colors.surface, color: theme.colors.txtPrincipal, padding: '8px', resize: 'vertical', fontSize: 12 }}
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 12 }}>
          <OutlinedButton $size="sm" onClick={onClose} disabled={loadingGuardar}>Cancelar</OutlinedButton>
          <OutlinedButton $size="sm" $variant="secondary" onClick={onSubmit} disabled={loadingGuardar}>
            <i className="bi bi-check2" /> {loadingGuardar ? 'Guardando...' : 'Guardar'}
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default ModalMantenimientoInicial;
